import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Typography, message } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import logoWiSecure from '@/assets/logo-wisecure-dark.svg';
import { queryClient } from '@/api/client';
import { useWisecureMutation } from '@wisecure/api-client/hooks';
import { useAuthStore } from '@/stores';
import { primary, neutral } from '@/theme';
import { fontWeight } from '@/theme/typography';
import { APP_VERSION } from '@/version';

const { Title, Text } = Typography;

interface FirebaseLoginRequest {
  email: string;
  password: string;
}

const styles = {
  root: {
    minHeight: '100vh',
    display: 'flex',
  } as React.CSSProperties,

  brandPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
    background: `linear-gradient(135deg, ${primary[700]} 0%, ${primary[400]} 100%)`,
    padding: 48,
    position: 'relative' as const,
    overflow: 'hidden',
  } as React.CSSProperties,

  brandContent: {
    position: 'relative' as const,
    zIndex: 1,
    textAlign: 'center' as const,
    color: neutral[0],
    maxWidth: 400,
  } as React.CSSProperties,

  brandLogo: {
    width: 120,
    height: 120,
    margin: '0 auto 32px',
  } as React.CSSProperties,

  brandTitle: {
    color: neutral[0],
    fontWeight: fontWeight.bold,
    fontSize: 32,
    lineHeight: 1.2,
    marginBottom: 16,
  } as React.CSSProperties,

  brandSubtitle: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: 16,
    lineHeight: 1.6,
  } as React.CSSProperties,

  formPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px 24px',
    background: neutral[0],
  } as React.CSSProperties,

  formWrapper: {
    width: '100%',
    maxWidth: 380,
  } as React.CSSProperties,

  formHeader: {
    marginBottom: 40,
  } as React.CSSProperties,

  formTitle: {
    fontWeight: fontWeight.semibold,
    fontSize: 24,
    color: neutral[900],
    marginBottom: 8,
  } as React.CSSProperties,

  formDescription: {
    color: neutral[500],
    fontSize: 14,
  } as React.CSSProperties,

  submitButton: {
    height: 44,
    borderRadius: 10,
    fontWeight: fontWeight.semibold,
    fontSize: 15,
    marginTop: 8,
  } as React.CSSProperties,

  input: {
    height: 44,
    borderRadius: 10,
  } as React.CSSProperties,

  floatingCircle1: {
    position: 'absolute' as const,
    width: 300,
    height: 300,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.04)',
    top: -80,
    right: -60,
  } as React.CSSProperties,

  floatingCircle2: {
    position: 'absolute' as const,
    width: 200,
    height: 200,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.04)',
    bottom: -40,
    left: -40,
  } as React.CSSProperties,

  mobileBrand: {
    display: 'none',
    textAlign: 'center' as const,
    marginBottom: 32,
  } as React.CSSProperties,

  responsiveStyle: `
    @media (max-width: 768px) {
      .login-brand-panel { display: none !important; }
      .login-form-panel { padding: 32px 24px !important; }
      .login-mobile-brand { display: block !important; }
    }
  `,
};

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [loading, setLoading] = useState(false);

  const mutation = useWisecureMutation(
    async (client, data: FirebaseLoginRequest) => {
      const credential = await signInWithEmailAndPassword(auth, data.email, data.password);
      const idToken = await credential.user.getIdToken();
      return client.auth.authControllerAuthenticateWithFirebase({
        requestBody: { firebaseIdToken: idToken },
      });
    },
    {
      onSuccess: (data) => {
        login(data.accessToken, {
          id: data.user.id,
          email: data.user.email,
          firstName: data.user.firstName ?? undefined,
          lastName: data.user.lastName ?? undefined,
          role: data.user.role,
        });
        queryClient.clear();
        message.success('Connexion réussie !');
        navigate('/');
      },
      onError: () => {
        message.error('Identifiants invalides.');
      },
      onSettled: () => setLoading(false),
    },
  );

  const onFinish = (values: FirebaseLoginRequest) => {
    setLoading(true);
    mutation.mutate(values);
  };

  return (
    <>
      <style>{styles.responsiveStyle}</style>
      <div style={styles.root}>
        <div className="login-brand-panel" style={styles.brandPanel}>
          <div style={styles.floatingCircle1} />
          <div style={styles.floatingCircle2} />
          <div style={styles.brandContent}>
            <img src={logoWiSecure} alt="WiSecure" style={styles.brandLogo} />
            <div style={styles.brandTitle}>WiSecure</div>
            <Text style={styles.brandSubtitle}>
              Plateforme de gestion de la sécurité.
              Contrôle et supervision en un seul endroit.
            </Text>
            <Text style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, marginTop: 24, display: 'block' }}>
              v{APP_VERSION}
            </Text>
          </div>
        </div>

        <div className="login-form-panel" style={styles.formPanel}>
          <div style={styles.formWrapper}>
            <div className="login-mobile-brand" style={styles.mobileBrand}>
              <img src={logoWiSecure} alt="WiSecure" style={{ width: 64, height: 64, marginBottom: 8 }} />
              <Title level={4} style={{ marginBottom: 0 }}>WiSecure</Title>
            </div>

            <div style={styles.formHeader}>
              <div style={styles.formTitle}>Connexion</div>
              <Text style={styles.formDescription}>
                Accédez à votre espace d'administration
              </Text>
            </div>

            <Form layout="vertical" onFinish={onFinish} autoComplete="off" size="large">
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: 'Veuillez saisir votre e-mail' },
                  { type: 'email', message: 'E-mail invalide' },
                ]}
              >
                <Input
                  prefix={<MailOutlined style={{ color: neutral[300] }} />}
                  placeholder="E-mail"
                  style={styles.input}
                />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[{ required: true, message: 'Veuillez saisir votre mot de passe' }]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: neutral[300] }} />}
                  placeholder="Mot de passe"
                  style={styles.input}
                />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                  style={styles.submitButton}
                >
                  Se connecter
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
}
